// ConfirmationModal.tsx
import React from "react"
import { Button, Modal } from "antd"
import DataQButton from "../Buttons/DataQButton.component"

/**
 * Props interface for the ConfirmationModal component
 */
interface ConfirmationModalProps {
  /** The title displayed at the top of the modal */
  title: string
  /** A boolean value indicating whether the modal is visible */
  isVisible: boolean
  /** Callback function to be executed when the cancel button is clicked */
  cancel: () => void
  /** Callback function to be executed when the confirm button is clicked - can optionally receive click event */
  confirm: (() => void) | ((e: React.MouseEvent<HTMLButtonElement>) => void)
  /** Text displayed on the confirm button. Defaults to "Confirm" if not provided */
  confirmButtonText?: string
  /** Type of the confirm button, determining its styling. Defaults to "secondary" if not provided */
  confirmButtonType?: "primary" | "secondary" | "success" | "danger" | "default"
  /** Message displayed in the body of the modal */
  message?: string
  /** Optional alternative action button (e.g., "Keep & Add" instead of cancel) */
  alternativeAction?: {
    text: string
    onClick: () => void
    buttonType?: "primary" | "secondary" | "success" | "danger" | "default"
  }
}

/**
 * ConfirmationModal component displays a modal dialog with customizable
 * title, cancel, and confirm buttons. The modal remains open until
 * either the cancel or confirm action is triggered.
 *
 * Cancel is outlined. A "danger" confirm is solid red, like Modal.confirm's
 * okType "danger", so every delete confirmation looks the same.
 *
 * @component
 * @param props - The props object for this component
 * @returns The rendered ConfirmationModal component
 *
 * @example
 * <ConfirmationModal
 *   title="Confirm Action"
 *   isVisible={true}
 *   cancel={() => console.log('Cancelled')}
 *   confirm={() => console.log('Confirmed')}
 *   confirmButtonText="Yes, Confirm"
 *   confirmButtonType="primary"
 *   message="Are you sure you want to confirm this action?"
 * />
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  title,
  isVisible,
  cancel,
  confirm,
  confirmButtonText = "Confirm",
  confirmButtonType = "secondary",
  message,
  alternativeAction,
}) => {
  /**
   * Handles the cancel button click
   */
  const handleCancel = (): void => {
    cancel()
  }

  /**
   * Handles the confirm button click
   */
  const handleConfirm = (e: React.MouseEvent<HTMLButtonElement>): void => {
    // Check if confirm function expects an event parameter
    if (confirm.length > 0) {
      // Function expects parameters, pass the event
      ;(confirm as (e: React.MouseEvent<HTMLButtonElement>) => void)(e)
    } else {
      // Function expects no parameters
      ;(confirm as () => void)()
    }
  }

  /**
   * Handles the alternative action button click
   */
  const handleAlternativeAction = (): void => {
    alternativeAction?.onClick()
  }

  return (
    <Modal
      centered
      title={title}
      open={isVisible}
      closable={false}
      footer={null}
    >
      <div className="flex flex-col mb-2">
        {message && <p className="text-md">{message}</p>}
      </div>

      <div className="flex flex-row space-x-2">
        <DataQButton
          className="ml-auto"
          type="secondary"
          onClick={handleCancel}
        >
          Cancel
        </DataQButton>
        {alternativeAction && (
          <DataQButton
            type={alternativeAction.buttonType || "secondary"}
            onClick={handleAlternativeAction}
          >
            {alternativeAction.text}
          </DataQButton>
        )}
        {confirmButtonType === "danger" ? (
          <Button type="primary" danger onClick={handleConfirm}>
            {confirmButtonText}
          </Button>
        ) : (
          <DataQButton type={confirmButtonType} onClick={handleConfirm}>
            {confirmButtonText}
          </DataQButton>
        )}
      </div>
    </Modal>
  )
}

export default ConfirmationModal
